import { Spacing } from '@persona-id/design-tokens';
import { TransferOutIcon } from '@persona-id/design-tokens/icons';
import { isEqual } from 'lodash';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import DisabledProductPage from 'dashboard/components/DisabledProductPage';
import { DISABLED_VERIFICATIONS_ANALYTICS_PAGE } from 'dashboard/components/DisabledProductPage/config';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import {
  RoutedTabbedSectionCard,
  RoutedTabbedSectionSwitch,
} from 'dashboard/components/TabbedSectionV2';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { QueryType, RelativeTimeSpan } from 'dashboard/models/analytics-query-template';
import { ExporterFileType } from 'dashboard/models/exporter';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { MetricsVerificationChecksOverTimeDatum } from 'dashboard/models/metrics';
import { VerificationStatusSummary } from 'dashboard/models/metrics-verification';
import { Template } from 'dashboard/models/template';
import {
  initialState,
  VerificationMetricsState,
} from 'dashboard/redux/slices/metrics-verification';
import DashboardVerificationAnalyticsChecksRouteV2 from 'dashboard/routes/DashboardVerificationAnalyticsChecksRouteV2/DashboardVerificationAnalyticsChecksRouteV2';
import { DashboardVerificationAnalyticsQueryTemplateRoute } from 'dashboard/routes/DashboardVerificationAnalyticsQueryTemplateRoute';
import DashboardVerificationAnalyticsVolumeRoute from 'dashboard/routes/DashboardVerificationAnalyticsVolumeRoute/DashboardVerificationAnalyticsVolumeRoute';
import VerificationExportConfirmationModal from 'dashboard/views/DashboardVerificationAnalyticsView/VerificationAnalyticsModal';
import Button from 'design-system/components/Button';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

import CreateQueryTemplateModal from '../../components/AnalyticsQueryContainer/CreateQueryTemplateModal';

export interface DashboardVerificationAnalyticsViewProps
  extends Pick<
    VerificationMetricsState,
    | 'filteredVolumeSummary'
    | 'verificationChecksOverTime'
    | 'queriedAt'
    | 'filters'
    | 'lastQueryFilters'
  > {
  productFeatureIsDisabled: boolean;
  handleExportData: (currentUrl: string, exporterFileType: ExporterFileType) => void;
  templates: (Template | InquiryTemplate)[];
  createQueryTemplate: (
    name: string,
    relativeTimeSpan: RelativeTimeSpan,
    queryType?: QueryType,
  ) => Promise<void>;
}

const ButtonGroup = styled('div')`
  display: flex;
  grid-column-start: 4;
  justify-content: flex-end;
  padding-right: ${Spacing[4]};
  > * + * {
    margin-left: ${Spacing[3]};
  }
`;
const LARGE_EXPORT_THRESHOLD = 250000;
const MAX_EXPORT_COUNT_LIMIT = 5000000;

const DashboardVerificationAnalyticsView = ({
  productFeatureIsDisabled,
  handleExportData,
  filteredVolumeSummary,
  lastQueryFilters,
  templates,
  verificationChecksOverTime,
  queriedAt,
  createQueryTemplate,
  filters,
}: DashboardVerificationAnalyticsViewProps) => {
  const [
    showTooLargeExportModal,
    { enable: openTooLargeExportModal, disable: hideTooLargeExportModal },
  ] = useToggle();

  const [showConfirmationModal, { enable: openConfirmationModal, disable: hideConfirmationModal }] =
    useToggle();

  const [
    showCreateQueryTemplateModal,
    { enable: openCreateQueryTemplateModal, disable: hideCreateQueryTemplateModal },
  ] = useToggle();

  // Must reference the URL to be sure that we only do the exports on Ver Volume to start
  const location = useLocation();
  const currentUrl = location.pathname;

  const handleCreateQueryTemplateAndClose = async (
    name: string,
    relativeTimeSpan: RelativeTimeSpan,
    queryType?: QueryType,
  ) => {
    await createQueryTemplate(name, relativeTimeSpan, queryType);
    hideCreateQueryTemplateModal();
  };

  // Gather a calculation on all of the known verifications so that we can give a good error message
  let totalVerifications = 0;
  let target = 'rows';
  let queryType = undefined;
  let HC_ARTICLE = undefined;
  switch (currentUrl) {
    case '/dashboard/verification-analytics/volume':
      totalVerifications = (filteredVolumeSummary.data || []).reduce(function (
        sum,
        summary: VerificationStatusSummary,
      ) {
        return sum + summary.created;
      }, 0);
      target = 'verifications';
      queryType = QueryType.VerificationsVolume;
      HC_ARTICLE =
        'https://help.withpersona.com/articles/7Mt5urHVKQbKirhyuzEexT/verification-volume-export/';
      break;

    case '/dashboard/verification-analytics/checks':
      // Iterate over the checks data model and get an estimate of how many checks we have in the given time window
      totalVerifications = (verificationChecksOverTime.data || []).reduce(function (
        overallSum,
        typeData: MetricsVerificationChecksOverTimeDatum,
      ) {
        return (
          overallSum +
          typeData.data.reduce(function (datumSum, typeObservation) {
            return (
              datumSum +
              typeObservation.data.reduce(function (checkSum, checkObservation) {
                return (
                  checkSum +
                  checkObservation.pass_count +
                  (checkObservation.not_applicable_count || 0) +
                  checkObservation.failure_count
                );
              }, 0)
            );
          }, 0)
        );
      }, 0);
      target = 'verification checks';
      queryType = QueryType.VerificationsChecks;
      HC_ARTICLE =
        'https://help.withpersona.com/articles/X6m9vL0l80BtWmb5pQRsM/verification-checks-export/';
      break;

    default:
      break;
  }

  const WARNING_MODAL_COPY = (
    <>
      You are exporting over {LARGE_EXPORT_THRESHOLD.toLocaleString()} {target}. This may take a
      long time. We recommend limiting the amount of verifications exported by setting filters.
    </>
  );

  const props = {
    openCreateQueryTemplateModal,
  };

  const estimatedDateRangeIntervalInDays = useMemo(() => {
    const { createdAtStart, createdAtEnd } = filters;
    const start = DateTime.fromISO(createdAtStart).startOf('day');
    const end = DateTime.fromISO(createdAtEnd).endOf('day');
    return Math.floor(end.diff(start, 'days').days);
  }, [filters.createdAtStart, filters.createdAtEnd]);

  if (productFeatureIsDisabled) {
    return (
      <DisabledProductPage
        id={DISABLED_VERIFICATIONS_ANALYTICS_PAGE.id}
        title={DISABLED_VERIFICATIONS_ANALYTICS_PAGE.title}
        headerTitle={DISABLED_VERIFICATIONS_ANALYTICS_PAGE.headerTitle}
        description={DISABLED_VERIFICATIONS_ANALYTICS_PAGE.description}
        uses={DISABLED_VERIFICATIONS_ANALYTICS_PAGE.uses}
        image={DISABLED_VERIFICATIONS_ANALYTICS_PAGE.image}
      />
    );
  }

  // If either flag is enabled, we'll render the module, but only enable it for the flags enabled
  // It also MUST have been queried for us to be able to hit the export button
  // It also MUST have data that is not null for us to be able ot export
  // By not NULL, we also mean NON ZERO
  const enabledButton =
    (currentUrl === '/dashboard/verification-analytics/volume' &&
      queriedAt.filteredVolume != null &&
      !isEqual(filteredVolumeSummary, initialState.filteredVolumeSummary) &&
      totalVerifications !== 0) ||
    (currentUrl === '/dashboard/verification-analytics/checks' &&
      queriedAt.checks != null &&
      !isEqual(verificationChecksOverTime, initialState.verificationChecksOverTime) &&
      totalVerifications !== 0);

  return (
    <Container>
      <InactiveEnvironmentBanner />
      <Header title='Analytics'>
        <ButtonGroup>
          <Permissioned permission={Permission.InquiriesExport}>
            <Button
              id='verifications-analytics__export'
              variant='basic'
              onClick={() => {
                totalVerifications < MAX_EXPORT_COUNT_LIMIT
                  ? openConfirmationModal()
                  : openTooLargeExportModal();
              }}
              disabled={!enabledButton}
              prefixIcon={<TransferOutIcon />}
            >
              Export
            </Button>
          </Permissioned>
        </ButtonGroup>
      </Header>
      <Section>
        <ModalWrapper isOpen={showConfirmationModal} handleCloseModal={hideConfirmationModal}>
          <VerificationExportConfirmationModal
            confirmAction={(exporterFileType: ExporterFileType) =>
              handleExportData(currentUrl, exporterFileType)
            }
            cancelAction={hideConfirmationModal}
            templates={templates}
            lastQueryFilters={lastQueryFilters}
            warningModal={totalVerifications > LARGE_EXPORT_THRESHOLD ? WARNING_MODAL_COPY : null}
            confirmButtonType={totalVerifications > LARGE_EXPORT_THRESHOLD ? 'danger' : 'primary'}
            helpCenterLink={HC_ARTICLE}
            includeMatchRequirements={queryType === QueryType.VerificationsChecks}
          />
        </ModalWrapper>
        <ModalWrapper isOpen={showTooLargeExportModal} handleCloseModal={hideTooLargeExportModal}>
          <ConfirmationModal
            confirmAction={hideTooLargeExportModal}
            cancelAction={hideTooLargeExportModal}
            header='Export size limit exceeded'
            confirmActionText='Revise filters'
            cancelActionText='Cancel'
          >
            You are attempting to export{' '}
            <strong>over {MAX_EXPORT_COUNT_LIMIT.toLocaleString()}</strong> {target}. This is more
            than the allowable number of objects to export. Please change the filters and try again.
          </ConfirmationModal>
        </ModalWrapper>
        <ModalWrapper
          isOpen={showCreateQueryTemplateModal}
          handleCloseModal={hideCreateQueryTemplateModal}
        >
          <CreateQueryTemplateModal
            cancelAction={hideCreateQueryTemplateModal}
            saveAction={handleCreateQueryTemplateAndClose}
            queryType={queryType}
            estimatedDateRangeIntervalInDays={estimatedDateRangeIntervalInDays}
          />
        </ModalWrapper>
        <RoutedTabbedSectionCard
          tabs={[
            {
              name: 'Volume',
              path: 'volume',
              dataTest: 'verification-analytics-view__tab--volume',
            },
            {
              name: 'Checks',
              path: 'checks',
              dataTest: 'verification-analytics-view__tab--checks',
            },
            {
              name: 'Saved Queries',
              path: 'templates',
              dataTest: 'verification-analytics-view__tab--templates',
            },
          ]}
        />
        <RoutedTabbedSectionSwitch
          tabs={[
            {
              path: 'volume',
              contents: <DashboardVerificationAnalyticsVolumeRoute {...props} />,
            },
            {
              path: 'checks',
              // We're removing the craziness from the Previous shared route. Not a good
              // idea for state shared between the two Redux stores. Wherever possible,
              // we should avoid this design pattern for a Products Analytics
              contents: <DashboardVerificationAnalyticsChecksRouteV2 {...props} />,
            },
            {
              path: 'templates',
              contents: <DashboardVerificationAnalyticsQueryTemplateRoute />,
            },
          ]}
        />
      </Section>
    </Container>
  );
};

export default DashboardVerificationAnalyticsView;
